import { Popover, Divider } from "antd";
import { TbCalculator } from "react-icons/tb";
import { LoanSchemaDto } from "dtos/loan.dto";
import { ComparisonPopoverContent } from "./ComparisonLoanamount";
import { MonthlyBreakdownChart } from "./MonthlyBreakdownChart";
import { useState } from "react";

type TrimmedLoanDto = Omit<LoanSchemaDto, "attachments">;

type Props = TrimmedLoanDto & {
  onViewAnalysis: (loan: TrimmedLoanDto) => unknown;
};

const PopoverContent = ({
  paid,
  expected,
  monthly_breakdown,
  payback_amount,
  onViewAnalysis,
}: {
  paid: number;
  expected: number | undefined;
  onViewAnalysis?: () => unknown;
} & Pick<LoanSchemaDto, "monthly_breakdown" | "payback_amount">) => {
  const hasChart = Boolean(monthly_breakdown?.length);
  const [showExtrapolated, setShowExtrapolated] = useState(false);

  const expectedFiltered = showExtrapolated
    ? expected
    : (monthly_breakdown?.filter((r) => !r.is_extrapolated).reduce((prev, curr) => prev + curr.mca_amount, 0) ?? 0);

  return (
    <div style={{ width: 280 }}>
      {hasChart && (
        <>
          <MonthlyBreakdownChart
            data={monthly_breakdown ?? []}
            showExtrapolated={showExtrapolated}
            setShowExtrapolated={setShowExtrapolated}
          />
          <Divider style={{ margin: "10px 0" }} />
        </>
      )}
      <ComparisonPopoverContent
        paid={paid}
        expected={expectedFiltered}
        extrapolated={expected !== expectedFiltered}
        payback={payback_amount}
        onViewAnalysis={onViewAnalysis}
      />
    </div>
  );
};

export const PaymentComparator = ({ onViewAnalysis, ...loan }: Props) => {
  const [open, setOpen] = useState(false);

  if (!loan.receivables) return null;

  const paidAmount = (loan.payback_amount ?? 0) - (loan.due_amount ?? 0);
  const expectedAmount = loan.total_mca_amount;

  let color: string | undefined = undefined;
  if (!expectedAmount) color = "lightgray";
  else if (paidAmount > expectedAmount) color = "indigo";
  else if (paidAmount < expectedAmount) color = "red";

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      content={
        <PopoverContent
          paid={paidAmount}
          expected={expectedAmount}
          payback_amount={loan.payback_amount}
          monthly_breakdown={loan.monthly_breakdown}
          onViewAnalysis={
            loan.expected_payment_breakdown && onViewAnalysis
              ? () => {
                  setOpen(false);
                  onViewAnalysis(loan);
                }
              : undefined
          }
        />
      }
      overlayInnerStyle={{ padding: "10px 12px" }}
    >
      <TbCalculator color={color} size={16} className="min-w-4" style={{ cursor: "pointer" }} />
    </Popover>
  );
};
